import { Injectable } from '@angular/core';
import {
  AuthResponse,
  ForgotPasswordPayload,
  LogInPayload,
  RefreshTokenResponse,
  ResetPasswordPayload,
  SignUpPayload,
} from '@core/models/entities/auth.models';
import { User } from '@core/models/entities/user.models';
import { ProfileApi } from './profile.api';
import { catchError, concatMap, EMPTY, map, Observable, of, OperatorFunction, tap } from 'rxjs';
import { HttpApi } from './http.api';
import { HttpGetOptions } from '@core/models/http.models';
import { Router } from '@angular/router';
import { AUTH_RC } from '@core/constants/routes.constants';
import { SchedulerCommandHistoryStorage } from '@core/persistence/scheduler-db.api';
import { AuthActions } from '@features/auth/store';
import { Store } from '@ngrx/store';

const ACCESS_TOKEN_KEY: string = 'access_token';
const REFRESH_TOKEN_KEY: string = 'refresh_token';
export const IMPERSONATION_BACKUP_KEY: string = 'impersonation_backup';

interface ImpersonationBackup {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
}

@Injectable({ providedIn: 'root' })
export class AuthApi {
  private _redirectUrl: string | null = null;

  constructor(
    private store: Store,
    private router: Router,
    private profileApi: ProfileApi,
    private readonly httpApi: HttpApi,
    private schedulerCommandHistoryStorage: SchedulerCommandHistoryStorage
  ) {}

  logIn(payload: LogInPayload): Observable<AuthResponse> {
    return this.httpApi.post<AuthResponse>(`auth/login`, payload).pipe(
      this.withResetSchedulerCommandHistoryStorage(),
      map((response: AuthResponse) => {
        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
        this.profileApi.user = response.user;
        return response;
      })
    );
  }

  impersonate(email: string): Observable<AuthResponse> {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      return EMPTY;
    }

    return this.httpApi.post<AuthResponse>(`auth/impersonate`, { email: normalizedEmail }, { skipSuccessMessage: true }).pipe(
      this.withResetSchedulerCommandHistoryStorage(),
      map((response: AuthResponse) => {
        this.persistImpersonationBackup();
        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
        this.profileApi.user = response.user;
        return response;
      })
    );
  }

  stopImpersonation(): void {
    const backup = this.readImpersonationBackup();
    if (!backup) {
      return;
    }

    this.setAccessToken(backup.accessToken);
    this.setRefreshToken(backup.refreshToken);
    this.profileApi.user = backup.user;
    this.clearImpersonationBackup();
  }

  isImpersonating(): boolean {
    return !!localStorage.getItem(IMPERSONATION_BACKUP_KEY);
  }

  get impersonatedUser(): User | null {
    if (!this.isImpersonating()) {
      return null;
    }
    return this.profileApi.user;
  }

  signUp(payload: SignUpPayload): Observable<AuthResponse> {
    return this.httpApi.post<AuthResponse>(`auth/sign-up`, payload).pipe(
      this.withResetSchedulerCommandHistoryStorage(),
      map((response: AuthResponse) => {
        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
        this.profileApi.user = response.user;
        return response;
      })
    );
  }

  setRedirectUrl(url: string): void {
    this._redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this._redirectUrl;
  }

  clearRedirectUrl(): void {
    this._redirectUrl = null;
  }

  clearLocalSession(): void {
    this.reset();
    this.store.dispatch(AuthActions.logout());
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    const refreshToken: string | null = this.getRefreshToken();

    return this.httpApi
      .post<RefreshTokenResponse>(`auth/refresh-access-token`, { refreshToken }, { skipSuccessMessage: true })
      .pipe(
        tap((response: RefreshTokenResponse) => {
          this.setAccessToken(response.accessToken);
          this.setRefreshToken(response.refreshToken);
        })
      );
  }

  refreshTokenReg(token: string): Observable<RefreshTokenResponse> {
    return this.httpApi.post<RefreshTokenResponse>(
      `auth/refresh-access-token`,
      { refreshToken: token },
      { skipSuccessMessage: true }
    );
  }

  logOut(successCallback?: () => void): Observable<boolean> {
    if (this.isImpersonating()) {
      this.stopImpersonation();
    }
    const onSuccess = () => {
      this.reset();
      this.router.navigate([`${AUTH_RC.root}/${AUTH_RC.children.logIn}`]);
      this.store.dispatch(AuthActions.logout());
      successCallback?.();
      return true;
    };
    if (!this.isAuthorized()) return of(onSuccess());
    return this.httpApi.post<boolean>(`auth/logout`, {}, { skipSuccessMessage: true }).pipe(
      this.withResetSchedulerCommandHistoryStorage(),
      tap({
        next: onSuccess,
        error: (err) => {
          if ([401, 402, 403].includes(err.status)) onSuccess();
        },
      })
    );
  }

  isAuthorized(): boolean {
    return !!this.getAccessToken();
  }

  forgotPassword(payload: ForgotPasswordPayload): Observable<boolean> {
    return this.httpApi.post<boolean>(`auth/forgot-password`, payload);
  }

  resetPassword(payload: ResetPasswordPayload, options?: HttpGetOptions): Observable<boolean> {
    return this.httpApi.patch<boolean>(`auth/reset-password`, payload, options);
  }

  private withResetSchedulerCommandHistoryStorage<T>(): OperatorFunction<T, T> {
    return concatMap((value: T) =>
      this.schedulerCommandHistoryStorage
        .clearAll()
        .pipe(map(() => value))
        .pipe(catchError(() => of(value)))
    );
  }

  private persistImpersonationBackup(): void {
    if (this.isImpersonating()) {
      return;
    }

    const backup: ImpersonationBackup = {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
      user: this.profileApi.user,
    };
    localStorage.setItem(IMPERSONATION_BACKUP_KEY, JSON.stringify(backup));
  }

  private readImpersonationBackup(): ImpersonationBackup | null {
    const raw = localStorage.getItem(IMPERSONATION_BACKUP_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as ImpersonationBackup;
    } catch {
      return null;
    }
  }

  private clearImpersonationBackup(): void {
    localStorage.removeItem(IMPERSONATION_BACKUP_KEY);
  }

  private setAccessToken(token: string | null): void {
    if (token) return localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  private setRefreshToken(token: string | null): void {
    if (token) return localStorage.setItem(REFRESH_TOKEN_KEY, token);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  private reset(): void {
    this.setAccessToken(null);
    this.setRefreshToken(null);
    this.profileApi.user = null;
    this.clearImpersonationBackup();
  }
}
